import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "../../lib/utils"

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-[var(--radius-xs)] px-1.5 h-5 text-[10px] font-semibold uppercase tracking-[0.10em] whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      // `tone` is the 26BET design-kit API (see checklist: "Badge: pill + tone
      // variants — neutral/brand/win/lose/info/live with pulsing dot").
      tone: {
        neutral: "bg-[var(--surface-3)] text-[var(--text-secondary)]",
        brand: "bg-[var(--gold-a20)] text-[var(--gold-400)]",
        solid: "bg-[var(--gold-500)] text-[var(--on-brand)]",
        win: "bg-[var(--green-a16)] text-[var(--green-500)]",
        lose: "bg-[var(--red-a16)] text-[var(--red-500)]",
        info: "bg-[var(--blue-a16)] text-[var(--blue-500)]",
        live: "bg-[var(--live-a16)] text-[var(--live-500)] shadow-[var(--glow-live)]",
      },
      pill: {
        true: "rounded-full px-2.5",
        false: "",
      },
    },
    defaultVariants: {
      tone: "neutral",
      pill: false,
    },
  }
)

function Badge({
  className,
  tone = "neutral",
  pill = false,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      data-tone={tone}
      className={cn(badgeVariants({ tone, pill }), className)}
      {...(props as React.ComponentPropsWithoutRef<"span">)}
    >
      {tone === "live" ? (
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full bg-current"
          style={{ animation: "byk-pulse 1.4s ease-in-out infinite" }}
        />
      ) : null}
      {children}
    </Comp>
  )
}

export { Badge, badgeVariants }
